import { groupBy } from 'lodash';

import { formatNumber } from '@reverbdotcom/commons/src/money';

import { MeasurementSystem, buildNewPackage } from '../../discovery/shipping_labels/shipping_label_package';
import { CARRIER_CALC_RATE_TYPE } from './components/shipping/ShippingForm';
import {
  core_apimessages_Money,
  core_apimessages_ImageEntry,
  core_apimessages_Listing_SeedType,
  HydrateSellForm,
  core_apimessages_Listing_ListingType,
  CoreSellFormNewListingLayout,
  core_apimessages_ShippingMethod as IShippingMethod,
  SellFormCanonicalProductCardData,
  core_apimessages_PreorderInfoEntry,
  core_apimessages_PreorderInfoEntry_TimeType,
  core_apimessages_PreorderInfoEntry_LeadTimeUnit,
  core_apimessages_ScheduledPriceDropEntry,
} from '../../../schemas/rql';
import { formatInternationalizedSeconds } from '../../../lib/date_utils';

export enum ActionType {
  UPDATE_FIELD = 'updateField',
  UPDATE_LOCALIZED_FIELD = 'updateLocalizedContentField',
  HANDLE_SUBMIT_SUCCESS = 'handleSubmit',
  SET_TRAIT = 'setTrait',
  CLEAR_TRAITS = 'clearTraits',
  UPDATE_SELECTED_VARIANT = 'updateSelectedVariant',
  SET_DEFAULT_SHIPPING = 'setDefaultShipping',
  SET_NO_SHIPPING = 'setNoShipping',
  SET_SHIPPING_PROFILE_ID = 'setShippingProfileId',
  ACTIVATE_CARRIER_CALCULATED_MODE = 'activateCarrierCalculatedMode',
  DEACTIVATE_CARRIER_CALCULATED_MODE = 'deactivateCarrierCalculatedMode',
  SET_HAS_INVENTORY = 'setHasInventory',
  SET_CONDITION_SLUG = 'setConditionSlug',
  SET_UPC_DOES_NOT_APPLY = 'setUpcDoesNotApply',
  SET_PREORDER_INFO_FIELD = 'setPreorderInfoField',
  SET_PREORDER_TIME_TYPE = 'setPreorderTimeType',
  TOGGLE_SUPPORTED_LOCALE_CONTENT = 'toggleSupportedLocaleContent',
  SET_SCHEDULED_PRICE_DROP_ACTIVE = 'setScheduledPriceDropActive',
  SET_SCHEDULED_PRICE_DROP_NEW_PRICE = 'setScheduledPriceDropNewPrice',
}

interface IAction {
  type: ActionType;
  fieldKey?: string;
  value?: any;
  forLocale?: string;
  backendListing?: HydrateSellForm.Fragment;
  shippingRates?: IShippingRateObjectState[];
  csp?: SellFormCanonicalProductCardData.Fragment;
  updateSeed?: boolean;
}

export enum Field {
  // Listing attributes
  ID = 'id',
  SKU = 'sku',
  YEAR = 'year',
  COUNTRY = 'countryOfOrigin',
  SEED_ID = 'seedId',
  SEED_TYPE = 'seedType',
  CANONICAL_PRODUCT_ID = 'canonicalProductId',
  ROOT_CATEGORY_UUID = 'categoryRootUuid',
  SUBCATEGORY_LEAF_UUIDS = 'categoryUuids',
  LOCALIZED_CONTENTS = 'localizedContents',
  HANDMADE = 'handmade',
  TRAITS = 'traits',
  CONDITION_SLUG = 'conditionSlug',
  HAS_INVENTORY = 'hasInventory',
  INVENTORY = 'inventory',
  UPC = 'upc',
  UPC_DOES_NOT_APPLY = 'upcDoesNotApply',
  VIDEO_LINK_URL = 'videoLinkUrl',
  PHOTOS = 'photos',
  SHIPPING_PROFILE_ID = 'shippingProfileId',
  OFFERS_LOCAL_PICKUP = 'offersLocalPickup',
  SHIPPING_RATES = 'shippingRates',
  CARRIER_CALCULATED_PACKAGE = 'carrierCalculatedPackage',
  PREFERS_REVERB_SHIPPING_LABEL = 'prefersReverbShippingLabel',
  SAME_DAY_SHIPPING_INELIGIBLE = 'sameDayShippingIneligible',
  PRICE = 'price',
  SELLER_COST = 'sellerCost',
  SELLER_REPORTED_MAP = 'sellerReportedMap',
  SOLD_AS_IS = 'soldAsIs',
  OFFERS_ENABLED = 'offersEnabled',
  TAX_EXEMPT = 'taxExempt',

  // Localized content attributes
  LOCALE = 'locale',
  TITLE = 'title',
  MAKE = 'make',
  MODEL = 'model',
  FINISH = 'finish',
  DESCRIPTION = 'description',
  PROP_65_WARNING = 'prop65Warning',
  DEFAULT_FOR_SHOP = 'defaultForShop',

  // Preorder Info
  PREORDER_INFO = 'preorderInfo',
  PREORDER_INFO_TIME_TYPE = 'timeType',
  PREORDER_INFO_LEAD_TIME_UNIT = 'leadTimeUnit',
  PREORDER_INFO_SHIP_DATE = 'shipDate',
  PREORDER_INFO_LEAD_TIME_DAYS = 'leadTimeDays',

  // Trait attributes
  TRAIT_UUID = 'keyId',
  TRAIT_VALUE_UUID = 'valueId',

  // Scheduled price drop attributes
  SCHEDULED_PRICE_DROPS = 'scheduledPriceDrops',
  SCHEDULED_PRICE_DROP_ACTIVE = 'active',
  SCHEDULED_PRICE_DROP_NEW_PRICE = 'newPrice',
}

export interface ILocalizedContentsState {
  [Field.LOCALE]: string;
  [Field.TITLE]: string;
  [Field.MAKE]: string;
  [Field.MODEL]: string;
  [Field.FINISH]: string,
  [Field.DESCRIPTION]: string;
  [Field.PROP_65_WARNING]: string;
  [Field.DEFAULT_FOR_SHOP]: boolean;
}

// All money fields in the sell form are localized number strings.
// These input will always be processed in the shop currency on the backend.
// When hydrating, we format incoming amounts according to the user's locale setting.
export interface IMoneyFieldState {
  amount: string;
}

export interface IShippingRateObjectState {
  regionCode: string;
  rate: IMoneyFieldState;
  incrementalRate: IMoneyFieldState;
  expeditedRate: IMoneyFieldState;
  rateType: string;
}

export interface IListingState {
  [Field.ID]: string;
  [Field.SKU]: string;
  [Field.YEAR]: string,
  [Field.COUNTRY]: string;
  [Field.SEED_ID]: string;
  [Field.SEED_TYPE]: core_apimessages_Listing_SeedType;
  [Field.ROOT_CATEGORY_UUID]: string;
  [Field.SUBCATEGORY_LEAF_UUIDS]: string[];
  [Field.LOCALIZED_CONTENTS]: ILocalizedContentsState[];
  [Field.HANDMADE]: boolean;
  [Field.TRAITS]: {
    [Field.TRAIT_UUID]: string;
    [Field.TRAIT_VALUE_UUID]: string;
  }[];
  [Field.CONDITION_SLUG]: string;
  [Field.HAS_INVENTORY]: boolean;
  [Field.INVENTORY]: number;
  [Field.UPC]: string;
  [Field.UPC_DOES_NOT_APPLY]: boolean;
  [Field.VIDEO_LINK_URL]: string;
  [Field.PHOTOS]: core_apimessages_ImageEntry[];
  [Field.SHIPPING_PROFILE_ID]: string;
  [Field.OFFERS_LOCAL_PICKUP]: boolean;
  [Field.SHIPPING_RATES]: IShippingRateObjectState[];
  [Field.CARRIER_CALCULATED_PACKAGE]: any;
  [Field.PREFERS_REVERB_SHIPPING_LABEL]: boolean;
  [Field.SAME_DAY_SHIPPING_INELIGIBLE]: boolean;
  [Field.PRICE]?: IMoneyFieldState;
  [Field.SELLER_COST]?: IMoneyFieldState;
  [Field.SELLER_REPORTED_MAP]?: IMoneyFieldState;
  [Field.SOLD_AS_IS]: boolean;
  [Field.OFFERS_ENABLED]: boolean;
  [Field.PREORDER_INFO]?: core_apimessages_PreorderInfoEntry;
  [Field.TAX_EXEMPT]?: boolean;
  [Field.SCHEDULED_PRICE_DROPS]: core_apimessages_ScheduledPriceDropEntry[];
}

export const EMPTY_PREORDER_INFO: core_apimessages_PreorderInfoEntry = {
  [Field.PREORDER_INFO_LEAD_TIME_DAYS]: 0,
  [Field.PREORDER_INFO_LEAD_TIME_UNIT]: core_apimessages_PreorderInfoEntry_LeadTimeUnit.DAYS,
  [Field.PREORDER_INFO_TIME_TYPE]: core_apimessages_PreorderInfoEntry_TimeType.SHIP_DATE,
  [Field.PREORDER_INFO_SHIP_DATE]: '',
};

export function generateListingTitle(seedListingData: CoreSellFormNewListingLayout.SeedListingData): string {
  return [
    seedListingData.make,
    seedListingData.model,
    seedListingData.year,
    seedListingData.finish ? `- ${seedListingData.finish}` : '',
  ].filter((v) => (v)).join(' ');
}

export function buildMoneyEntry(moneyObject?: core_apimessages_Money): IMoneyFieldState {
  if (!moneyObject) { return null; }

  return {
    amount: formatNumber(Number(moneyObject.amount)),
  };
}

function buildPhotosStateFromPersistedListing(existingListing: HydrateSellForm.Fragment): core_apimessages_ImageEntry[] {
  const { images = [] } = existingListing;
  return images.map((existingPhoto) => {
    const {
      id,
      publicId,
      version,
      transformation: transformationResponse,
      height,
      width,
    } = existingPhoto;

    return {
      id,
      publicId,
      version,
      height,
      width,
      transformation: transformationResponse ? {
        width: transformationResponse.width,
        height: transformationResponse.height,
        x: transformationResponse.x,
        y: transformationResponse.y,
        crop: transformationResponse.crop,
        angle: transformationResponse.angle,
      } : null,
    };
  });
}

function buildShippingRatesStateFromPersistedListing(existingListing: HydrateSellForm.Fragment): IShippingRateObjectState[] {
  if (existingListing.shippingProfileId) { return []; }
  if (existingListing.allShippingPrices.shippingPrices.length < 1) { return []; }

  const methodsByRegion = groupBy(existingListing.allShippingPrices.shippingPrices, 'shippingRegionCode');

  return Object.keys(methodsByRegion).map((regionCode) => {
    const standard = methodsByRegion[regionCode].find((m) => m.shippingMethod === IShippingMethod.SHIPPED);
    const combined = methodsByRegion[regionCode].find((m) => m.shippingMethod === IShippingMethod.COMBINED_SHIPPING);
    const expedited = methodsByRegion[regionCode].find((m) => m.shippingMethod === IShippingMethod.EXPEDITED_SHIPPING);

    return {
      regionCode,
      rate: standard.carrierCalculated ? null : buildMoneyEntry(standard?.originalRate),
      incrementalRate: buildMoneyEntry(combined?.originalRate),
      expeditedRate: buildMoneyEntry(expedited?.originalRate),
      rateType: standard.carrierCalculated ? CARRIER_CALC_RATE_TYPE : null,
    };
  });
}

function buildPackageStateFromPersistedListing(existingListing: HydrateSellForm.Fragment) {
  const { shipmentPackage } = existingListing;
  if (!shipmentPackage) { return null; }

  const packageFormAttributes = {};
  ['length', 'width', 'height', 'weight'].forEach((dimension) => {
    packageFormAttributes[dimension] = {
      value: shipmentPackage[dimension].value,
      unit: shipmentPackage[dimension].unit,
    };
  });

  return packageFormAttributes;
}

function buildPreorderInfoStateFromPersistedListing(existingListing: HydrateSellForm.Fragment): core_apimessages_PreorderInfoEntry {
  const { preorderInfo } = existingListing;

  if (preorderInfo?.shipDate?.seconds) {
    return ({
      [Field.PREORDER_INFO_TIME_TYPE]: core_apimessages_PreorderInfoEntry_TimeType.SHIP_DATE,
      [Field.PREORDER_INFO_SHIP_DATE]: formatInternationalizedSeconds(preorderInfo.shipDate.seconds, 'YYYY-MM-DD'),
      [Field.PREORDER_INFO_LEAD_TIME_DAYS]: null,
      [Field.PREORDER_INFO_LEAD_TIME_UNIT]: core_apimessages_PreorderInfoEntry_LeadTimeUnit.DAYS,
    });
  }

  if (preorderInfo?.leadTimeDays) {
    return ({
      [Field.PREORDER_INFO_TIME_TYPE]: core_apimessages_PreorderInfoEntry_TimeType.LEAD_TIME,
      [Field.PREORDER_INFO_SHIP_DATE]: null,
      [Field.PREORDER_INFO_LEAD_TIME_DAYS]: preorderInfo.leadTimeDays,
      [Field.PREORDER_INFO_LEAD_TIME_UNIT]: core_apimessages_PreorderInfoEntry_LeadTimeUnit.DAYS,
    });
  }

  return { ...EMPTY_PREORDER_INFO };
}

function buildScheduledPriceDropStateFromPersistedListing(existingListing: HydrateSellForm.Fragment): core_apimessages_ScheduledPriceDropEntry[] {
  const { scheduledPriceDrops } = existingListing;

  return scheduledPriceDrops.map((drop) => {
    return {
      [Field.SCHEDULED_PRICE_DROP_ACTIVE]: drop.active,
      [Field.SCHEDULED_PRICE_DROP_NEW_PRICE]: buildMoneyEntry(drop.newPrice),
    };
  });
}

export function buildFormStateFromPersistedListing(existingListing: HydrateSellForm.Fragment): IListingState {
  return {
    [Field.ID]: existingListing[Field.ID],
    [Field.SKU]: existingListing[Field.SKU],
    [Field.YEAR]: existingListing[Field.YEAR],
    [Field.COUNTRY]: existingListing[Field.COUNTRY],
    [Field.SEED_ID]: existingListing[Field.SEED_ID],
    [Field.SEED_TYPE]: existingListing[Field.SEED_TYPE],
    [Field.ROOT_CATEGORY_UUID]: existingListing[Field.ROOT_CATEGORY_UUID],
    [Field.SUBCATEGORY_LEAF_UUIDS]: existingListing.categories.filter((c) => c.leaf).map((c) => c.id),
    [Field.LOCALIZED_CONTENTS]: existingListing[Field.LOCALIZED_CONTENTS].map((lc) => ({
      [Field.LOCALE]: lc[Field.LOCALE],
      [Field.MAKE]: lc[Field.MAKE],
      [Field.MODEL]: lc[Field.MODEL],
      [Field.FINISH]: lc[Field.FINISH],
      [Field.TITLE]: lc[Field.TITLE],
      [Field.DESCRIPTION]: lc[Field.DESCRIPTION],
      [Field.PROP_65_WARNING]: lc[Field.PROP_65_WARNING],
      [Field.DEFAULT_FOR_SHOP]: lc[Field.DEFAULT_FOR_SHOP],
    })),
    [Field.HANDMADE]: existingListing[Field.HANDMADE],
    [Field.TRAITS]: existingListing[Field.TRAITS].map((t) => ({
      [Field.TRAIT_UUID]: t[Field.TRAIT_UUID],
      [Field.TRAIT_VALUE_UUID]: t[Field.TRAIT_VALUE_UUID],
    })),
    [Field.CONDITION_SLUG]: existingListing.condition?.conditionSlug || '',
    [Field.HAS_INVENTORY]: existingListing[Field.HAS_INVENTORY],
    [Field.INVENTORY]: existingListing[Field.INVENTORY],
    [Field.UPC]: existingListing[Field.UPC],
    [Field.UPC_DOES_NOT_APPLY]: existingListing[Field.UPC_DOES_NOT_APPLY],
    [Field.VIDEO_LINK_URL]: existingListing.video?.link || '',
    [Field.PHOTOS]: buildPhotosStateFromPersistedListing(existingListing),
    [Field.SHIPPING_PROFILE_ID]: existingListing[Field.SHIPPING_PROFILE_ID],
    [Field.OFFERS_LOCAL_PICKUP]: existingListing.allShippingPrices.localPickup,
    [Field.SHIPPING_RATES]: buildShippingRatesStateFromPersistedListing(existingListing),
    [Field.CARRIER_CALCULATED_PACKAGE]: buildPackageStateFromPersistedListing(existingListing),
    [Field.PREFERS_REVERB_SHIPPING_LABEL]: existingListing[Field.PREFERS_REVERB_SHIPPING_LABEL],
    [Field.SAME_DAY_SHIPPING_INELIGIBLE]: existingListing[Field.SAME_DAY_SHIPPING_INELIGIBLE],
    [Field.PRICE]: buildMoneyEntry(existingListing.sellerPrice),
    [Field.SELLER_COST]: buildMoneyEntry(existingListing[Field.SELLER_COST]),
    [Field.SELLER_REPORTED_MAP]: buildMoneyEntry(existingListing[Field.SELLER_REPORTED_MAP]),
    [Field.SOLD_AS_IS]: existingListing[Field.SOLD_AS_IS],
    [Field.OFFERS_ENABLED]: existingListing.listingType === core_apimessages_Listing_ListingType.MAKE_AN_OFFER,
    [Field.PREORDER_INFO]: buildPreorderInfoStateFromPersistedListing(existingListing),
    [Field.TAX_EXEMPT]: existingListing[Field.TAX_EXEMPT],
    [Field.SCHEDULED_PRICE_DROPS]: buildScheduledPriceDropStateFromPersistedListing(existingListing),
  };
}

export function formReducer(state: IListingState, action: IAction): IListingState {
  switch (action.type) {
    case ActionType.UPDATE_FIELD:
      return {
        ...state,
        [action.fieldKey]: action.value,
      };

    case ActionType.UPDATE_LOCALIZED_FIELD:
      return {
        ...state,
        [Field.LOCALIZED_CONTENTS]: state[Field.LOCALIZED_CONTENTS].map((lc) => ({
          ...lc,
          ...(action.forLocale === lc.locale && {
            [action.fieldKey]: action.value,
          }),
        })),
      };

    case ActionType.HANDLE_SUBMIT_SUCCESS:
      return buildFormStateFromPersistedListing(action.backendListing);

    case ActionType.SET_TRAIT: {
      // Remove existing trait if it matches the uuid of the updated trait
      const newTraits = [...state[Field.TRAITS]].filter((existingTrait) => existingTrait.keyId !== action.value.uuid);
      // Add new trait uuid and value to the array
      newTraits.push({ keyId: action.value.uuid, valueId: action.value.traitValue });
      return { ...state, [Field.TRAITS]: newTraits };
    }

    case ActionType.CLEAR_TRAITS:
      return { ...state, [Field.TRAITS]: [] };

    case ActionType.UPDATE_SELECTED_VARIANT:
      return {
        ...state,
        [Field.SEED_TYPE]: action.updateSeed ? core_apimessages_Listing_SeedType.CanonicalProduct : state[Field.SEED_TYPE],
        [Field.SEED_ID]: action.updateSeed ? action.value?.id : state[Field.SEED_ID],
        [Field.YEAR]: action.value?.year,
        [Field.COUNTRY]: action.value?.countryOfOrigin,
        [Field.LOCALIZED_CONTENTS]: state[Field.LOCALIZED_CONTENTS].map((lc) => {
          return {
            ...lc,
            ...(action.forLocale === lc.locale && {
              [Field.MAKE]: action.csp?.brand?.name,
              [Field.MODEL]: action.value?.model,
              [Field.FINISH]: action.value?.finish,
              [Field.TITLE]: generateListingTitle({
                make: action.csp?.brand?.name,
                model: action.value?.model,
                year: action.value?.year,
                finish: action.value?.finish,
              }),
            }),
          };
        }),
      };

    case ActionType.SET_DEFAULT_SHIPPING:
      return {
        ...state,
        [Field.SHIPPING_RATES]: action.shippingRates,
        [Field.SHIPPING_PROFILE_ID]: '',
        [Field.PREFERS_REVERB_SHIPPING_LABEL]: true,
        [Field.CARRIER_CALCULATED_PACKAGE]: null,
      };

    case ActionType.SET_NO_SHIPPING:
      return {
        ...state,
        [Field.SHIPPING_RATES]: [],
        [Field.SHIPPING_PROFILE_ID]: '',
        [Field.PREFERS_REVERB_SHIPPING_LABEL]: false,
        [Field.CARRIER_CALCULATED_PACKAGE]: null,
      };

    case ActionType.SET_SHIPPING_PROFILE_ID:
      return {
        ...state,
        [Field.SHIPPING_RATES]: [],
        [Field.SHIPPING_PROFILE_ID]: action.value,
        [Field.PREFERS_REVERB_SHIPPING_LABEL]: false,
        [Field.OFFERS_LOCAL_PICKUP]: false,
        [Field.CARRIER_CALCULATED_PACKAGE]: null,
      };

    case ActionType.ACTIVATE_CARRIER_CALCULATED_MODE:
      return {
        ...state,
        [Field.SHIPPING_RATES]: action.shippingRates,
        [Field.PREFERS_REVERB_SHIPPING_LABEL]: false,
        [Field.CARRIER_CALCULATED_PACKAGE]: buildNewPackage(MeasurementSystem.IMPERIAL),
      };

    case ActionType.DEACTIVATE_CARRIER_CALCULATED_MODE:
      return {
        ...state,
        [Field.SHIPPING_RATES]: action.shippingRates,
        [Field.PREFERS_REVERB_SHIPPING_LABEL]: true,
        [Field.CARRIER_CALCULATED_PACKAGE]: null,
      };

    case ActionType.SET_HAS_INVENTORY:
      return {
        ...state,
        [Field.HAS_INVENTORY]: action.value,
        [Field.INVENTORY]: action.value ? state.inventory : null,
      };

    case ActionType.SET_CONDITION_SLUG:
      return {
        ...state,
        [Field.CONDITION_SLUG]: action.value,
        [Field.HAS_INVENTORY]: false,
        [Field.INVENTORY]: null,
        [Field.UPC_DOES_NOT_APPLY]: true,
        [Field.UPC]: '',
      };

    case ActionType.SET_UPC_DOES_NOT_APPLY:
      return {
        ...state,
        [Field.UPC_DOES_NOT_APPLY]: action.value,
        [Field.UPC]: action.value ? '' : state.upc,
      };
    case ActionType.SET_PREORDER_TIME_TYPE:
      return ({
        ...state,
        [Field.PREORDER_INFO]: {
          ...EMPTY_PREORDER_INFO,
          [Field.PREORDER_INFO_TIME_TYPE]: action.value,
        },
      });
    case ActionType.SET_PREORDER_INFO_FIELD:
      return ({
        ...state,
        [Field.PREORDER_INFO]: {
          ...state[Field.PREORDER_INFO],
          [action.fieldKey]: action.value,
        },
      });
    case ActionType.TOGGLE_SUPPORTED_LOCALE_CONTENT:
      if (action.value) { // Add locale content
        return {
          ...state,
          [Field.LOCALIZED_CONTENTS]: [
            ...state[Field.LOCALIZED_CONTENTS],
            {
              // Copy default locale data to prefill for the new locale
              ...state[Field.LOCALIZED_CONTENTS].find((lc) => lc.defaultForShop),
              [Field.LOCALE]: action.forLocale,
              [Field.DESCRIPTION]: '',
              [Field.PROP_65_WARNING]: '',
              [Field.DEFAULT_FOR_SHOP]: false,
            },
          ],
        };
      }
      return { // Remove locale content
        ...state,
        [Field.LOCALIZED_CONTENTS]: [
          ...state[Field.LOCALIZED_CONTENTS].filter((lc) => lc.locale !== action.forLocale),
        ],
      };
    case ActionType.SET_SCHEDULED_PRICE_DROP_ACTIVE:
      return {
        ...state,
        [Field.SCHEDULED_PRICE_DROPS]: [
          {
            ...state[Field.SCHEDULED_PRICE_DROPS][0],
            [Field.SCHEDULED_PRICE_DROP_ACTIVE]: action.value,
          },
        ],
      };
    case ActionType.SET_SCHEDULED_PRICE_DROP_NEW_PRICE:
      return {
        ...state,
        [Field.SCHEDULED_PRICE_DROPS]: [
          {
            ...state[Field.SCHEDULED_PRICE_DROPS][0],
            [Field.SCHEDULED_PRICE_DROP_NEW_PRICE]: action.value,
          },
        ],
      };

    default:
      return { ...state };
  }
}
